import React from 'react';
import { MapPin, Navigation } from 'lucide-react';

/**
 * Public-side "Locate Gravesite" section.
 *
 * Renders only if the memorial has cemetery info AND the admin's visibility
 * setting allows showing it for the current viewer. The backend already
 * strips the GPS coords + row/plot/notes for non-approved viewers when
 * visibility is "family" — the component just trusts what arrived.
 *
 * Builds a Google Maps directions URL on every platform (Google Maps app
 * handles the deep-link on Android and iOS; falls back to maps.google.com
 * in the browser). Apple Maps is offered too on iOS via `maps.apple.com`.
 */
export function LocateGravesite({ memorial }) {
  if (!memorial) return null;

  const hasPin = memorial.gravesite_lat != null && memorial.gravesite_lng != null;
  const hasAddress = !!(memorial.cemetery_name || memorial.cemetery_address);

  if (!hasPin && !hasAddress) return null;

  const addressLine = [
    memorial.cemetery_address,
    memorial.cemetery_city,
    memorial.cemetery_region,
    memorial.cemetery_country,
  ].filter(Boolean).join(', ');

  // Destination string for directions URL — prefer GPS, fall back to address.
  const destination = hasPin
    ? `${memorial.gravesite_lat},${memorial.gravesite_lng}`
    : encodeURIComponent(
        `${memorial.cemetery_name || ''} ${addressLine || ''}`.trim(),
      );

  // Google Maps universal directions URL — works as a deep link on Android,
  // opens the Google Maps app on iOS if installed, falls back to web.
  const googleDirections = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;

  // Apple Maps URL — opens Maps app on iOS / macOS; harmless on others.
  const appleDirections = `https://maps.apple.com/?daddr=${destination}`;

  // OSM static map preview for the embed when we have a pin. No API key.
  const previewSrc = hasPin
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${Number(memorial.gravesite_lng) - 0.003}%2C${Number(memorial.gravesite_lat) - 0.0015}%2C${Number(memorial.gravesite_lng) + 0.003}%2C${Number(memorial.gravesite_lat) + 0.0015}&layer=mapnik&marker=${memorial.gravesite_lat}%2C${memorial.gravesite_lng}`
    : null;

  // iOS detection — show Apple Maps as a secondary option for Apple users.
  // Intentionally excludes macOS Safari ("Mac OS X" without iPad/iPhone), since
  // Apple Maps deep-links don't add value on a desktop browser.
  const isiOS = typeof navigator !== 'undefined' &&
    /iPhone|iPad|iPod/.test(navigator.userAgent || '');

  const sectionLabel = [
    memorial.cemetery_section && `Section ${memorial.cemetery_section}`,
    memorial.gravesite_row && `Row ${memorial.gravesite_row}`,
    memorial.gravesite_plot && `Plot ${memorial.gravesite_plot}`,
  ].filter(Boolean).join(' · ');

  return (
    <section
      id="locate"
      className="ml-section ml-container"
      data-testid="public-locate-gravesite"
    >
      <div className="rounded-md border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--surface))] overflow-hidden">
        <div className="grid md:grid-cols-[1.05fr_1fr]">
          <div className="p-8 sm:p-10">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[hsl(var(--accent))]">
              Locate Gravesite
            </div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-[hsl(var(--primary))] mt-3 leading-tight">
              Visit them.
            </h2>

            <div className="mt-6 space-y-2 text-[15px] text-[hsl(var(--primary))]/85 leading-relaxed">
              {memorial.cemetery_name && (
                <div className="font-medium text-[hsl(var(--primary))]" data-testid="grav-public-cemetery-name">
                  {memorial.cemetery_name}
                </div>
              )}
              {addressLine && (
                <div className="text-[13.5px]" data-testid="grav-public-cemetery-address">
                  {addressLine}
                </div>
              )}
              {sectionLabel && (
                <div className="text-[13px] mt-2 text-[hsl(var(--primary))]/75" data-testid="grav-public-section-row-plot">
                  {sectionLabel}
                </div>
              )}
              {memorial.gravesite_notes && (
                <p className="mt-4 text-[13px] italic text-[hsl(var(--primary))]/70 leading-relaxed max-w-prose" data-testid="grav-public-notes">
                  {memorial.gravesite_notes}
                </p>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={googleDirections}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-btn-gold"
                data-testid="grav-get-directions-btn"
              >
                <Navigation className="w-4 h-4" />
                Get Directions
              </a>
              {isiOS && (
                <a
                  href={appleDirections}
                  className="ml-btn-outline"
                  data-testid="grav-apple-maps-btn"
                >
                  Open in Apple Maps
                </a>
              )}
            </div>
          </div>

          <div className="relative bg-[hsl(var(--primary)/0.08)] min-h-[260px]">
            {previewSrc ? (
              <iframe
                title={`Map preview · ${memorial.cemetery_name || 'Gravesite'}`}
                src={previewSrc}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full border-0"
                data-testid="grav-public-map-preview"
              />
            ) : (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center text-center p-8"
                data-testid="grav-public-no-pin"
              >
                <MapPin className="w-8 h-8 text-[hsl(var(--accent))]" strokeWidth={1.4} />
                <p className="mt-3 text-[13px] text-[hsl(var(--primary))]/70 max-w-xs leading-relaxed">
                  No exact pin yet — Get Directions will route you to the cemetery address.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
